import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Icon } from "@iconify/react/dist/iconify.js";

const ApiHealthChart = ({ data, errorDetails, summary }) => {
  const [errorType, setErrorType] = useState('server'); // 'server' or 'client'
  const [selectedError, setSelectedError] = useState(null);
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'text-green-500';
      case 'degraded':
        return 'text-orange-500';
      case 'unhealthy':
        return 'text-error';
      default:
        return 'text-gray-500';
    }
  };

  // Calculate totals for display
  const totalRequests = data?.reduce((sum, item) => sum + (item.totalRequests || 0), 0) || 0;
  const totalServerErrors = data?.reduce((sum, item) => sum + (item.serverErrors || 0), 0) || 0;
  const totalClientErrors = data?.reduce((sum, item) => sum + (item.clientErrors || 0), 0) || 0;
  
  // Calculate average response time
  const avgResponseTime = data?.length && totalRequests > 0
    ? data.reduce((sum, item) => sum + (item.avgDuration || 0) * (item.totalRequests || 0), 0) / totalRequests
    : 0;
    
  // Process error details
  const currentErrors = errorType === 'server' 
    ? (errorDetails?.serverErrors || [])
    : (errorDetails?.clientErrors || []);
    
  // Group errors by URL and method to get counts
  const groupedErrors = currentErrors.reduce((acc, error) => {
    const key = `${error.method || 'GET'}-${error.url}`;
    if (!acc[key]) {
      acc[key] = {
        method: error.method || 'GET',
        url: error.url,
        count: 0,
        days: new Set(),
        // Track daily counts
        dailyCounts: {}
      };
    }
    acc[key].count++;
    if (error.day) {
      acc[key].days.add(error.day);
      // Increment daily count for this error
      if (!acc[key].dailyCounts[error.day]) {
        acc[key].dailyCounts[error.day] = 0;
      }
      acc[key].dailyCounts[error.day]++;
    }
    return acc;
  }, {});
  
  const errorsList = Object.values(groupedErrors).sort((a, b) => b.count - a.count);
  
  // Filter data based on selected error
  const getFilteredData = () => {
    if (!selectedError) return data;
    
    return data.map(dayData => {
      const dayStr = dayData.day;
      const dailyCount = selectedError.dailyCounts[dayStr] || 0;
      
      return {
        ...dayData,
        highlightServerErrors: errorType === 'server' ? dailyCount : 0,
        highlightClientErrors: errorType === 'client' ? dailyCount : 0
      };
    });
  };
  
  const filteredData = getFilteredData();

  return (
    <div className="flex flex-col xl:flex-row w-full h-fit xl:h-[400px] gap-4 xl:gap-2">
      <div className="flex flex-col gap-2 card-container rounded-md p-2 sm:p-3 w-full xl:w-3/5">
        {/* Chart Section - Left Half */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center mb-2">
          <div className="text-base xl:text-lg font-semibold text-font-primary">Backend API</div>
          <div className="flex flex-row items-center gap-2 md:gap-3">
            <div className="flex items-center gap-1 text-sm text-font-secondary">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#8884d8' }}></div>
              <span>Requests (Y-Axis Left)</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-font-secondary">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ff0000' }}></div>
              <span>Server Errors (Y-Axis Right)</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-font-secondary">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ffa500' }}></div>
              <span>Client Errors (Y-Axis Right)</span>
            </div>
            {selectedError && (
              <div className="flex items-center gap-1 text-sm text-font-secondary">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#3189E0' }}></div>
                <span>Selected Error</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full h-full">
          <div className="flex flex-row flex-wrap items-center justify-between text-sm text-font-secondary">
            
            <div className="text-sm text-font-secondary">Requests: <span className="font-medium text-primary">{totalRequests}</span></div>
            <div className="text-sm text-font-secondary">Avg. Response: <span className="font-medium text-secondary">{Math.round(avgResponseTime)}ms</span></div>
            <div className="text-sm text-font-secondary">Server Errors: <span className="font-medium text-error">{totalServerErrors}</span></div>
            <div className="text-sm text-font-secondary">Client Errors: <span className="font-medium text-orange-500">{totalClientErrors}</span></div>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredData} margin={{ top: 10, right: -25, left: -15, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis 
                dataKey="day" 
                tickFormatter={formatDate} 
                stroke="#aaa"
              />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <Tooltip 
                formatter={(value, name) => [value, name]}
                labelFormatter={formatDate}
              />
              <Bar yAxisId="left" dataKey="totalRequests" name="Requests" fill="#8884d8" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="serverErrors" name="Server Errors" fill="#ff0000" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="right" dataKey="clientErrors" name="Client Errors" fill="#ffa500" radius={[4, 4, 0, 0]} />
              {selectedError && errorType === 'server' && (
                <Bar yAxisId="right" dataKey="highlightServerErrors" name="Selected Error" fill="#3189E0" radius={[4, 4, 0, 0]} />
              )}
              {selectedError && errorType === 'client' && (
                <Bar yAxisId="right" dataKey="highlightClientErrors" name="Selected Error" fill="#3189E0" radius={[4, 4, 0, 0]} />
              )}
            </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Error Details Section - Right Half */}
      <div className="w-full xl:w-2/5 flex flex-col scrollable rounded-md px-2">
          <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <div 
              className={`px-3 py-1 rounded-md cursor-pointer text-sm ${errorType === 'server' ? 'text-error button-container' : 'bg-transparent text-font-secondary shadow-none'}`}
              onClick={() => { setErrorType('server'); setSelectedError(null); }}
            >
              Server Errors
            </div>
            <div 
              className={`px-3 py-1 rounded-md cursor-pointer text-sm ${errorType === 'client' ? 'text-orange-500 button-container' : 'bg-transparent text-font-secondary shadow-none'}`}
              onClick={() => { setErrorType('client'); setSelectedError(null); }}
            >
              Client Errors
            </div>
          </div>
          {selectedError && (
            <button 
              className="text-sm text-secondary hover:text-primary transition-colors flex items-center gap-1"
              onClick={() => setSelectedError(null)}
            >
              <Icon icon="mdi:refresh" className="text-lg" />
              Reset
            </button>
          )}
      </div>
          
          <div className="overflow-y-auto scrollable-item flex-grow">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left py-2 text-xs text-font-secondary">URL</th>
                  <th className="text-right py-2 text-xs text-font-secondary">Count</th>
                </tr>
              </thead>
              <tbody>
                {errorsList.map((error, index) => (
                  <tr 
                    key={index} 
                    className={`border-t border-bgAccent hover:bg-bgAccent cursor-pointer ${selectedError === error ? 'bg-bgAccent' : ''}`}
                    onClick={() => setSelectedError(error)}
                  >
                    <td className="py-2 text-sm truncate max-w-[200px]" title={error.url}>{error.url}</td>
                    <td className="text-right py-2 text-sm">{error.count}</td>
                  </tr>
                ))}
                {errorsList.length === 0 && (
                  <tr className="border-t border-gray-200">
                    <td colSpan={3} className="text-center py-4 text-sm">No {errorType} errors to display</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};

export default ApiHealthChart;